import React from 'react';
import { useDispatch } from 'react-redux';
import { HotelTileGroupComponent } from '@model/content';
import Grid from '@mui/material/Grid';
import { Theme } from '@theme/base';
import { withTheme } from '@emotion/react';
import { FiltersSideBarType } from '@model/filters';
import { OfferType } from '@model/offers/offers';
import { DestinationLinkBuilder, LinkItem } from '@model/iceberg/geography';
import { resetSelectedFilters } from '@state/slices/filters';
import { resetDealFinder } from '@state/slices/deal-finder/dealFinderSlice';
import { FromPriceByPathProductTile } from '@components/common/product-tile/from';
import { SectionTitleSizes } from '@components/common/section-title';
import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import { Carousel } from '@components/common';
import { Logging } from '@util/logging';

/* ***************** *
 *       Types       *
 * ***************** */
export interface HotelTileGroupProps extends HotelTileGroupComponent {
  theme?: Theme;
  testId?: string;
  titleSize?: SectionTitleSizes;
}

/* ********************************** *
 *    ProductPackageGroupComponent    *
 * ********************************** */

const getMediumTileSizeConfig = (size: number = 0) => {
  switch (size) {
    case 1:
      return 12;
    case 2:
      return 6;
    case 3:
      return 4;
    case 4:
      return 3;
    default:
      return 12;
  }
};

export const HotelTileComponent = (props: HotelTileGroupProps) => {
  const { title, subTitle, ctaTitle, ctaPath, hotels, path } = props;
  const t: any = useI18NextContext();
  const dispatch = useDispatch();
  const resetFilters = () => {
    dispatch(resetSelectedFilters(FiltersSideBarType.SEARCH));
    dispatch(resetDealFinder());
  };

  if (!hotels.length) {
    return null;
  }

  const shouldDeepLink = Object.values(OfferType).includes(path as OfferType);

  /* *** RENDERERS *** */
  const productCount = hotels.length;
  const renderProductPackageTiles: () => Array<JSX.Element> = () =>
    hotels
      .map(
        (product: any) =>
          product && (
            <Grid key={product.connectId} item xs={12} sm={6} md={getMediumTileSizeConfig(productCount)}>
              <FromPriceByPathProductTile productPackage={product} deepLink={shouldDeepLink} />
            </Grid>
          )
      )
      .filter((productTile: any) => productTile);

  const productPackageTiles: Array<JSX.Element> = renderProductPackageTiles();

  if (!productPackageTiles || !productPackageTiles.length) {
    Logging.error({
      text: `[ProductPackageGroup][${title}] no valid products in the CMS `
    });
    return null;
  }

  const linkBuilder: DestinationLinkBuilder = new DestinationLinkBuilder();
  const hotelsLink: LinkItem = linkBuilder.getDestinationHotelsLink(ctaPath);
  const link = ctaTitle && ctaPath ? { label: t(ctaTitle), linkItem: hotelsLink, onClick: resetFilters } : undefined;

  return (
    <>
      <Carousel testId={props.testId || TestId.hotelTileGroup.main} title={title} subTitle={subTitle || ''} link={link}>
        {productPackageTiles}
      </Carousel>
    </>
  );
};

export const HotelTileGroup = withTheme(HotelTileComponent);
